// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';
import { Box, Button, Grid2, Stack } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseInput, BaseInputAddress, BaseInputPhone, Loading, UnderDevelopment } from '~/components/common';
// Others
import { BACK_TO_PREVIOUS_PAGE, EMPTY_STRING } from '~/utils/constants/common';
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBodyUpdateContact, IContactForm } from '~/utils/interface/contact';
import { IBaseAddress } from '~/utils/interface/common';
import { createContact, getContactDetail, updateContact } from '~/thunks/contact/contactThunk';
import { removeEmptyObjects } from '~/utils/helper/common';
// Styles, images, icons
import styles from './FormContact.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormContact = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/contact', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: contactId } = useParams();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<IContactForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const handleGetContactDetail = useCallback((contactId: string) => {
    setIsLoading(true);

    dispatch(getContactDetail(contactId))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        const { id, ...remainingData } = data;

        if (!remainingData?.latitude) delete remainingData.latitude;

        if (!remainingData?.longitude) delete remainingData.longitude;

        const convertData = {
          ...remainingData,
          email: data?.email || EMPTY_STRING,
          phoneNumber: data?.phoneNumber || EMPTY_STRING,
          address: data?.address || EMPTY_STRING,
          address2: data?.address2 || EMPTY_STRING,
          city: data?.city || EMPTY_STRING,
          state: data?.state || EMPTY_STRING,
          zipCode: data?.zipCode || EMPTY_STRING,
          country: data?.country || EMPTY_STRING,
          countryCode: data?.countryCode || EMPTY_STRING,
        };

        reset(convertData);
      })
      .catch((_err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, reset]);

  useEffect(() => {
    if (!contactId) return;

    handleGetContactDetail(contactId);
  }, [contactId, handleGetContactDetail]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleCancelFormContact = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
    reset(defaultValuesForm);
  };

  const handleAddContact = async (data: IContactForm) => {
    setIsLoading(true);

    const body: IContactForm = removeEmptyObjects(data) as IContactForm;

    dispatch(createContact(body))
      .unwrap()
      .then(() => {
        handleCancelFormContact();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditContact = (data: IContactForm) => {
    setIsLoading(true);

    const params: IBodyUpdateContact = {
      id: contactId || EMPTY_STRING,
      body: data,
    };

    dispatch(updateContact(params))
      .unwrap()
      .then(() => {
        handleCancelFormContact();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitFormContact = (data: IContactForm) => {
    if (contactId) {
      handleEditContact(data);
    } else {
      handleAddContact(data);
    }
  };

  const handleSelectAddress = (value: IBaseAddress) => {
    const { address, city, state, zipCode, country, latitude, longitude } = value;

    setValue('address', address || EMPTY_STRING, { shouldValidate: true });
    setValue('city', city || EMPTY_STRING, { shouldValidate: true });
    setValue('state', state || EMPTY_STRING, { shouldValidate: true });
    setValue('zipCode', zipCode || EMPTY_STRING, { shouldValidate: true });
    setValue('country', country || EMPTY_STRING, { shouldValidate: true });
    setValue('latitude', latitude, { shouldValidate: true });
    setValue('longitude', longitude, { shouldValidate: true });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formContactPage'
      title={t(contactId ? 'form_contact_title_edit' : 'form_contact_title_add')}
      onBackToPreviousPage={handleCancelFormContact}
    >
      <>
        <Stack
          component={'form'}
          spacing={'20px'}
          className={cx('form')}
          onSubmit={handleSubmit(handleSubmitFormContact)}
        >
          <Box component={'div'} className={cx('formBody')}>
            <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
              <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                <Grid2 size={6}>
                  <Controller
                    name='firstName'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        required
                        label={t('form_contact_label_first_name')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.firstName?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='lastName'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        required
                        label={t('form_contact_label_last_name')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.lastName?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='email'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        label={t('form_contact_label_email')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.email?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 size={6}>
                  <Controller
                    name='phoneNumber'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInputPhone
                        label={t('form_contact_label_phone_number')}
                        value={value}
                        onChange={onChange}
                        errorMessage={errors?.phoneNumber?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
                  <Grid2 size={4}>
                    <Controller
                      name='address'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInputAddress
                          label={t('form_contact_label_address')}
                          value={value}
                          onChange={onChange}
                          onSelectAddress={handleSelectAddress}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='address2'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInputAddress
                          label={t('field_address_second_label')}
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='city'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput label={t('form_contact_label_city')} value={value} onChange={onChange} />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='state'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput label={t('form_contact_label_state')} value={value} onChange={onChange} />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='zipCode'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_contact_label_zip_code')}
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <Controller
                      name='country'
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <BaseInput
                          label={t('form_contact_label_country')}
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>
            </Grid2>
          </Box>

          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={handleCancelFormContact}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit' disabled={contactId ? !isDirty : false}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        </Stack>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </>
    </ChildrenPage>
  );
};

export default FormContact;
